import { useEffect, useId, useRef, type ReactNode } from 'react';
import { ago, fmtDateTime, reportingTz } from '../lib/format';

/** The accent motif: a small square. filled = primary/HQ, outline = secondary/other office. */
export function Sq({ kind = 'filled', tone, label }: { kind?: 'filled' | 'outline'; tone?: string; label?: string }) {
  return <span className={`sq ${kind}${tone ? ` tone-${tone}` : ''}`} aria-hidden={label ? undefined : true} aria-label={label} role={label ? 'img' : undefined} />;
}

/** A studio's logo, served from this app (never hot-linked from the studio's site). */
export function StudioLogo({ src, size = 'small' }: { src: string | null | undefined; size?: 'small' | 'large' }) {
  if (!src) return null;
  return <img className={`studio-logo ${size}`} src={src} alt="" loading="lazy" decoding="async" />;
}

export function Badge({ children, tone, title }: { children: ReactNode; tone?: string; title?: string }) {
  return (
    <span className={`badge${tone ? ` tone-${tone}` : ''}`} title={title}>
      {children}
    </span>
  );
}

export function DemoBadge() {
  return (
    <span className="badge demo" title="Fictional demo record (reserved .example domain). Not a real company.">
      DEMO
    </span>
  );
}

export function DemoNotice({ children }: { children?: ReactNode }) {
  return (
    <p className="demo-notice">
      <span className="badge demo">DEMO DATA</span>{' '}
      {children ?? 'Some records shown here are fictional demo studios on reserved .example domains. They are not real companies.'}
    </p>
  );
}

export function Time({ iso, relative }: { iso: string | null | undefined; relative?: boolean }) {
  if (!iso) return <span className="muted">—</span>;
  return (
    <time dateTime={iso} title={`${fmtDateTime(iso)} (${reportingTz()}) · ${iso}`} className="num">
      {relative ? ago(iso) : fmtDateTime(iso)}
    </time>
  );
}

export function TzNote() {
  return <span className="muted small">Times in {reportingTz()}</span>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="empty">{children}</p>;
}

export function ErrorMsg({ children }: { children: ReactNode }) {
  if (!children) return null;
  return (
    <p className="msg err" role="alert">
      {children}
    </p>
  );
}

export function Loading({ what = 'Loading' }: { what?: string }) {
  return (
    <p className="loading muted" role="status">
      {what}…
    </p>
  );
}

export function Unknown({ children = 'unknown' }: { children?: ReactNode }) {
  return <span className="unknown">{children}</span>;
}

export function Section({ title, children, aside, id }: { title: ReactNode; children: ReactNode; aside?: ReactNode; id?: string }) {
  return (
    <section className="section" id={id}>
      <header className="section-head">
        <h2>
          <Sq /> {title}
        </h2>
        {aside && <div className="section-aside">{aside}</div>}
      </header>
      {children}
    </section>
  );
}

/** Page title; `meta` is the context line under it (scope, status, time zone). */
export function PageHead({ title, meta, children }: { title: ReactNode; meta?: ReactNode; children?: ReactNode }) {
  return (
    <header className="page-head">
      <div className="page-head-text">
        <h1>{title}</h1>
        {meta && <p className="page-meta">{meta}</p>}
      </div>
      {children && <div className="page-actions">{children}</div>}
    </header>
  );
}

/** Modal dialog: Esc closes, focus moves in and is restored, Tab is trapped.
 *  The key listener is registered once (M-090) and reads the latest onClose through a ref. */
export function Modal({
  title,
  onClose,
  children,
  footer,
  wide,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const id = useId();

  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>('[autofocus], input, select, textarea, button');
    (first ?? ref.current)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (!ref.current) return;
      if (e.key === 'Escape') {
        // the feedback dialog sits above; let it handle its own Esc
        if (document.querySelector('.fb-dialog')) return;
        e.preventDefault();
        closeRef.current();
      }
      if (e.key === 'Tab') {
        const nodes = ref.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (!nodes.length || document.querySelector('.fb-dialog')) return;
        const a = nodes[0];
        const z = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === a) {
          e.preventDefault();
          z.focus();
        } else if (!e.shiftKey && document.activeElement === z) {
          e.preventDefault();
          a.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      before?.focus?.();
    };
  }, []);

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={ref} className={`modal${wide ? ' wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby={`${id}-t`} tabIndex={-1}>
        <header className="modal-head">
          <h2 id={`${id}-t`}>{title}</h2>
          <button type="button" className="btn ghost small" onClick={onClose} aria-label="Close dialog">
            Close
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer && <footer className="modal-foot">{footer}</footer>}
      </div>
    </div>
  );
}

export function Tabs<T extends string>({ tabs, value, onChange }: { tabs: { id: T; label: ReactNode }[]; value: T; onChange: (v: T) => void }) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((t) => (
        <button
          key={t.id}
          role="tab"
          type="button"
          aria-selected={value === t.id}
          className={value === t.id ? 'tab active' : 'tab'}
          onClick={() => onChange(t.id)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function KV({ rows }: { rows: [ReactNode, ReactNode][] }) {
  return (
    <dl className="kv">
      {rows.map(([k, v], i) => (
        <div key={i} className="kv-row">
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function StatusMark({ status }: { status: string }) {
  const tone =
    status === 'failed' ? 'bad' : status === 'partial' || status === 'waiting_review' || status === 'paused' ? 'warn' : status === 'running' || status === 'active' ? 'live' : status === 'succeeded' ? 'ok' : 'idle';
  const kind = status === 'queued' || status === 'cancelled' || status === 'ended' || status === 'paused' ? 'outline' : 'filled';
  return (
    <span className={`status tone-${tone}`}>
      <Sq kind={kind} tone={tone} /> {status.replace('_', ' ')}
    </span>
  );
}
